import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { social, type Follow, type Leader } from "@/api/social";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
} from "@/components/ui";

export function DiscoverPage() {
  const leaders = useQuery({ queryKey: ["leaders"], queryFn: () => social.leaders() });
  const follows = useQuery({ queryKey: ["my-follows"], queryFn: () => social.myFollows() });
  const followingIds = new Set(
    (follows.data ?? []).filter((f) => !f.stopped_at).map((f) => f.leader_trader_id),
  );

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-base font-semibold text-term-fg">Discover traders</h1>
        <p className="text-2xs text-term-mute">
          Public traders you can copy. Each follow lets you set copy factor + a
          drawdown auto-stop. Your account size stays yours; we proportionally
          replicate the leader's orders.
        </p>
      </div>

      <Card>
        <CardBody className="p-0">
          {leaders.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !leaders.data || leaders.data.length === 0 ? (
            <EmptyState
              title="No public traders yet"
              description="When traders opt in to publish their profile, they show up here."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Trader</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Bio</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {leaders.data.map((l) => (
                  <LeaderRow
                    key={l.trader_id}
                    leader={l}
                    following={followingIds.has(l.trader_id)}
                    follow={(follows.data ?? []).find(
                      (f) => f.leader_trader_id === l.trader_id && !f.stopped_at,
                    )}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <p className="text-2xs text-term-mute">
        See your active follows under Account → Follows.
      </p>
    </div>
  );
}

function LeaderRow({
  leader,
  following,
  follow,
}: {
  leader: Leader;
  following: boolean;
  follow: Follow | undefined;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const startFollow = useMutation({
    mutationFn: () =>
      social.follow({ leader_trader_id: leader.trader_id, copy_factor: 1.0 }),
    onSuccess: () => {
      toast.push("Now following", "success");
      qc.invalidateQueries({ queryKey: ["my-follows"] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "follow failed";
      toast.push(msg, "error");
    },
  });
  const stopFollow = useMutation({
    mutationFn: () => social.unfollow(follow!.id),
    onSuccess: () => {
      toast.push("Stopped following", "info");
      qc.invalidateQueries({ queryKey: ["my-follows"] });
    },
  });
  return (
    <tr className="border-t border-term-border">
      <td className="px-3 py-1.5 text-term-fg">{leader.display_name}</td>
      <td className="px-3 py-1.5 text-term-dim">{leader.bio ?? "—"}</td>
      <td className="px-3 py-1.5">
        {leader.verified && <Badge tone="good">verified</Badge>}
      </td>
      <td className="px-3 py-1.5 text-right">
        {following ? (
          <Button variant="ghost" onClick={() => stopFollow.mutate()}>
            Stop
          </Button>
        ) : (
          <Button onClick={() => startFollow.mutate()} loading={startFollow.isPending}>
            Follow
          </Button>
        )}
      </td>
    </tr>
  );
}
